import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { collection, getDocs, query, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { format, subDays } from "date-fns";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Modal,
  IconButton,
  TextField,
  Chip,
  Alert,
  Pagination,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import StoreIcon from '@mui/icons-material/Store'; // İkon eklendi
import ShareIcon from "@mui/icons-material/Share";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// --- ŞUBE LİSTESİ ---
const subeler = [
  { key: 'sefakoy_merkez', label: 'Sefaköy Merkez' },
  { key: 'sefakoy_sube', label: 'Sefaköy Şube' },
  { key: 'kavakli_sube', label: 'Kavaklı Şube' }
];

function FirmaBakiyeler() { // sube prop'u kaldırıldı, içeriden yönetilecek
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const subeFromUrl = queryParams.get("sube")?.trim();

  // Şube State Yönetimi
  const [selectedSube, setSelectedSube] = useState(subeFromUrl || (subeler.length > 0 ? subeler[0].key : ''));

  const [bakiyeler, setBakiyeler] = useState([]);
  const [selectedFirma, setSelectedFirma] = useState(null);
  const [islemler, setIslemler] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const tableShareRef = useRef(null);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);

  // Şube Değişim Handler'ı
  const handleSubeChange = (newSubeKey) => {
    setSelectedSube(newSubeKey);
    setBakiyeler([]); // Tabloyu temizle
    setSelectedFirma(null); // Detay açıksa kapat
  };

  const fetchData = async () => {
    if (!selectedSube) return; // Şube seçili değilse işlem yapma

    setLoading(true);
    setError("");
    try {
      const q = query(collection(db, "subeler", selectedSube, "islemler"));
      const snapshot = await getDocs(q);
      const firmaMap = {};

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const docId = docSnap.id;

        let firmaIsmi = null;
        if (data.firma && data.firma.firmaIsmi && data.firma.firmaIsmi.trim()) {
          firmaIsmi = data.firma.firmaIsmi.trim();
        }
        if (!firmaIsmi) return;

        if (!firmaMap[firmaIsmi]) {
          firmaMap[firmaIsmi] = {
            toplamHas: 0,
            sonIslemTarihi: null,
            sonOdeme: 0,
            odemeler15Gun: 0,
            islemler: [],
          };
        }

        const timestamp = data.timestamp?.toDate() || new Date();
        const hasEsdeger = parseFloat(data.hasEsdeger) || 0;

        if (hasEsdeger > 0) {
          const isGiris = ["Giriş", "Alış", "İade", "Değişim"].includes(data.islemTuru);
          const isCikis = ["Çıkış", "Satış"].includes(data.islemTuru);

          if (isGiris) {
            firmaMap[firmaIsmi].toplamHas += hasEsdeger;
          } else if (isCikis) {
            firmaMap[firmaIsmi].toplamHas -= hasEsdeger;
            firmaMap[firmaIsmi].sonOdeme = hasEsdeger;

            const now = new Date();
            if (timestamp >= subDays(now, 15)) {
              firmaMap[firmaIsmi].odemeler15Gun += hasEsdeger;
            }
          }
        }

        if (!firmaMap[firmaIsmi].sonIslemTarihi || timestamp > firmaMap[firmaIsmi].sonIslemTarihi) {
          firmaMap[firmaIsmi].sonIslemTarihi = timestamp;
        }

        let hasMiktar = 0;
        const gram = parseFloat(data.gram) || 0;
        const adet = parseFloat(data.adet) || 0; 
        const milyem = parseFloat(data.milyem) || 0;
        
        const toplamTutar = parseFloat(data.toplamTutar) || 0;
        const nakit = parseFloat(data.nakit) || 0;
        const havaleEft = parseFloat(data.havaleEft) || 0;
        const krediKarti = parseFloat(data.krediKarti) || 0;

        if (data.kategori === "Hurda" || data.kategori === "Takı Ürün") {
          hasMiktar = gram * milyem;
        } else if (data.kategori === "Sarrafiye" || data.kategori === "Gram Altın") {
          hasMiktar = gram * adet;
        }

        firmaMap[firmaIsmi].islemler.push({
          id: docId,
          tarih: format(timestamp, "dd.MM.yyyy"),
          urun: data.urun || "-",
          gram,
          adet,
          milyem,
          hasMiktar,
          toplamTutar,
          nakit,
          havaleEft,
          krediKarti,
          islemTuru: data.islemTuru || "-",
          hasEsdeger,
          rawTimestamp: timestamp 
        });
      });
      
      Object.values(firmaMap).forEach(firma => {
        firma.islemler.sort((a, b) => b.rawTimestamp - a.rawTimestamp);
      });

      const list = Object.entries(firmaMap)
        .map(([firmaIsmi, veri]) => {
          const oncekiBakiye = veri.toplamHas + veri.odemeler15Gun; 
          const odemeYuzdesi = oncekiBakiye > 0 ? ((veri.odemeler15Gun / oncekiBakiye) * 100).toFixed(1) : 0;
          return {
            firmaIsmi,
            toplamHas: parseFloat(veri.toplamHas.toFixed(2)),
            sonIslemTarihi: veri.sonIslemTarihi ? format(veri.sonIslemTarihi, "dd.MM.yyyy") : "-",
            sonOdeme: veri.sonOdeme.toFixed(2),
            odemeYuzdesi,
            islemler: veri.islemler,
          };
        })
        .sort((a, b) => a.firmaIsmi.localeCompare(b.firmaIsmi));

      setBakiyeler(list);
      setLoading(false);
    } catch (err) {
      setError("Veri alınamadı: " + err.message);
      setLoading(false);
    }
  };

  // selectedSube değiştiğinde veriyi yeniden çek
  useEffect(() => {
    fetchData();
  }, [selectedSube]);

  const handleOpenDetay = (firma) => {
    setSelectedFirma(firma);
    setIslemler(firma.islemler);
    setCurrentPage(1); 
  };

  const handleSaveHasEsdeger = async (islemId) => {
    const value = parseFloat(editValue);
    if (isNaN(value) || value < 0) {
      setError("Geçerli bir sayı girin.");
      return;
    }

    try {
      await updateDoc(doc(db, "subeler", selectedSube, "islemler", islemId), {
        hasEsdeger: value,
      });

      const updatedIslemler = islemler.map((i) =>
        i.id === islemId ? { ...i, hasEsdeger: value } : i
      );
      setIslemler(updatedIslemler);

      setBakiyeler((prev) =>
        prev.map((f) => {
          if (f.firmaIsmi === selectedFirma.firmaIsmi) {
            const yeniToplam = updatedIslemler.reduce((sum, i) => {
              const hareket = ["Giriş", "Alış", "İade", "Değişim"].includes(i.islemTuru)
                ? i.hasEsdeger
                : -i.hasEsdeger;
              return sum + hareket;
            }, 0);

            let yeniOdemeler15Gun = f.odemeler15Gun || 0;
            const isCikis = ["Çıkış", "Satış"].includes(
              updatedIslemler.find((i) => i.id === islemId)?.islemTuru || ""
            );
            if (isCikis) {
              yeniOdemeler15Gun += value; 
            }

            const oncekiBakiye = yeniToplam + yeniOdemeler15Gun;
            const yeniOdemeYuzdesi = oncekiBakiye > 0 ? ((yeniOdemeler15Gun / oncekiBakiye) * 100).toFixed(1) : 0;

            return {
              ...f,
              toplamHas: parseFloat(yeniToplam.toFixed(2)),
              sonOdeme: value.toFixed(2),
              odemeYuzdesi: yeniOdemeYuzdesi,
              islemler: updatedIslemler,
            };
          }
          return f;
        })
      );

      setEditId(null);
      setEditValue("");
      setError("");
    } catch (err) {
      setError("Kaydedilemedi: " + err.message);
    }
  };
  
  const formatPaymentInfo = (i) => {
    if (i.toplamTutar === 0) return "0 TL";

    const parts = [];
    if (i.nakit > 0) parts.push(`${i.nakit.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL (Nakit)`);
    if (i.havaleEft > 0) parts.push(`${i.havaleEft.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL (Havale)`);
    if (i.krediKarti > 0) parts.push(`${i.krediKarti.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL (K.K)`);
    
    if (parts.length === 0) return `${i.toplamTutar.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL`;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', fontSize: '0.75rem' }}>
        {parts.map((part, index) => (
          <span key={index}>{part}</span>
        ))}
      </Box>
    );
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentIslemler = islemler.slice(indexOfFirstItem, indexOfLastItem);
  const pageCount = Math.ceil(islemler.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const totalBakiye = bakiyeler.reduce((acc, firm) => acc + firm.toplamHas, 0);
  const isShareMenuOpen = Boolean(shareAnchorEl);

  const handleOpenShareMenu = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleCloseShareMenu = () => {
    setShareAnchorEl(null);
  };

  const captureTableCanvas = async () => {
    if (!tableShareRef.current) {
      throw new Error("Tablo alanı bulunamadı.");
    }
    return html2canvas(tableShareRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true
    });
  };

  const handleDownloadPng = async () => {
    try {
      const canvas = await captureTableCanvas();
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `firma-bakiyeleri-${selectedSube || "sube"}.png`;
      link.click();
    } catch (err) {
      alert(`Goruntu olusturulamadi: ${err.message}`);
    } finally {
      handleCloseShareMenu();
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const canvas = await captureTableCanvas();
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const y = imgHeight > pageHeight - 40 ? 20 : (pageHeight - imgHeight) / 2;

      pdf.addImage(imgData, "PNG", 20, y, imgWidth, imgHeight);
      pdf.save(`firma-bakiyeleri-${selectedSube || "sube"}.pdf`);
    } catch (err) {
      alert(`PDF olusturulamadi: ${err.message}`);
    } finally {
      handleCloseShareMenu();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" color="error" align="center">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="fb-page" sx={{ background: "linear-gradient(165deg, #f5f2eb 0%, #ece6da 45%, #e2dacd 100%)", minHeight: "auto", p: 1.5 }}>
      
      {/* ŞUBE SEÇİM BUTONLARI (YENİ EKLENDİ) */}
      <Box className="fb-sube-wrap" sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        {subeler.map((subeItem) => (
          <Button
            key={subeItem.key}
            className="fb-sube-btn"
            variant={selectedSube === subeItem.key ? "contained" : "outlined"}
            onClick={() => handleSubeChange(subeItem.key)}
            startIcon={<StoreIcon />}
            sx={{
              fontWeight: 700,
              fontSize: '0.88rem',
              px: 2.6,
              py: 0.85,
              borderRadius: 2.5,
              background: selectedSube === subeItem.key ? 'linear-gradient(180deg, #1f2937 0%, #0f172a 100%)' : 'rgba(255,255,255,0.9)',
              color: selectedSube === subeItem.key ? '#f8fafc' : '#0f172a',
              borderColor: 'rgba(212,175,98,0.45)',
              '&:hover': {
                background: selectedSube === subeItem.key ? 'linear-gradient(180deg, #273549 0%, #152235 100%)' : 'rgba(255,255,255,1)',
                borderColor: 'rgba(212,175,98,0.6)'
              }
            }}
          >
            {subeItem.label}
          </Button>
        ))}
      </Box>

      <Typography className="fb-title" variant="h5" fontWeight={700} textAlign="center" mb={1.2} color="#0f172a" sx={{ letterSpacing: "0.01em" }}>
        Firma Bakiyeleri - {subeler.find(s => s.key === selectedSube)?.label || selectedSube}
      </Typography>

      <Box className="fb-toolbar" sx={{ display: "flex", justifyContent: "flex-end", mb: 1, mt: -4 }}>
        <Button
          className="fb-share-btn"
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleOpenShareMenu}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.84rem",
            px: 2.2,
            py: 0.65,
            borderRadius: 2,
            background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)",
            border: "1px solid rgba(212,175,98,0.45)",
            "&:hover": { background: "linear-gradient(180deg, #273549 0%, #152235 100%)" }
          }}
        >
          Paylas
        </Button>
      </Box>
      <Menu anchorEl={shareAnchorEl} open={isShareMenuOpen} onClose={handleCloseShareMenu}>
        <MenuItem onClick={handleDownloadPng}>
          <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>PNG Olarak Indir</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadPdf}>
          <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>PDF Olarak Indir</ListItemText>
        </MenuItem>
      </Menu>

      <TableContainer
        ref={tableShareRef}
        className="fb-table-wrap"
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid rgba(212,175,98,0.25)",
          boxShadow: "0 14px 30px rgba(15,23,42,0.14)",
          maxHeight: "68vh",
          overflowY: "auto"
        }}
      >
        <Table className="fb-table" stickyHeader>
          <TableHead>
            <TableRow sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)" }}>
              <TableCell sx={{ color: "#f8fafc", fontWeight: 800, letterSpacing: "0.03em" }}>Firma</TableCell>
              <TableCell sx={{ color: "#f8fafc", fontWeight: 800, letterSpacing: "0.03em" }} align="right">Bakiye</TableCell>
              <TableCell sx={{ color: "#f8fafc", fontWeight: 800, letterSpacing: "0.03em" }} align="center">Son İşlem Tarihi</TableCell>
              <TableCell sx={{ color: "#f8fafc", fontWeight: 800, letterSpacing: "0.03em" }} align="center">Son Ödeme (g)</TableCell>
              <TableCell sx={{ color: "#f8fafc", fontWeight: 800, letterSpacing: "0.03em" }} align="center">Yüzdelik Ödeme</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bakiyeler.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">Kayıtlı firma bakiyesi bulunamadı.</TableCell>
              </TableRow>
            ) : (
              <>
                {bakiyeler.map((f) => (
                  <TableRow
                    key={f.firmaIsmi}
                    hover
                    onClick={() => handleOpenDetay(f)}
                    sx={{
                      cursor: "pointer",
                      "&:nth-of-type(odd)": { backgroundColor: "rgba(248, 250, 252, 0.9)" },
                      "&:nth-of-type(even)": { backgroundColor: "rgba(241, 245, 249, 0.75)" },
                      "&:hover": { backgroundColor: "rgba(212,175,98,0.12)" }
                    }}
                  >
                    <TableCell className="fb-cell-name" data-label="Firma" sx={{ fontWeight: 600 }}>{f.firmaIsmi}</TableCell>
                    <TableCell className="fb-cell-balance" data-label="Bakiye" align="right" sx={{ fontWeight: "bold", color: f.toplamHas < 0 ? "#166534" : "#d32f2f" }}>
                      {f.toplamHas.toFixed(2)} Gram
                    </TableCell>
                    <TableCell className="fb-cell-date" data-label="Son İşlem" align="center">{f.sonIslemTarihi}</TableCell>
                    <TableCell className="fb-cell-payment" data-label="Son Ödeme" align="center" sx={{ fontWeight: "bold" }}>{f.sonOdeme}</TableCell>
                    <TableCell className="fb-cell-percent" data-label="Ödeme %" align="center">
                      <Chip
                        label={`%${f.odemeYuzdesi}`}
                        color={parseFloat(f.odemeYuzdesi) >= 80 ? "success" : parseFloat(f.odemeYuzdesi) >= 50 ? "warning" : "error"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                
                <TableRow className="fb-total-row" sx={{ bgcolor: "rgba(226, 232, 240, 0.82)", borderTop: "2px solid rgba(148,163,184,0.8)" }}>
                  <TableCell className="fb-total-label" data-label="Toplam" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#1e293b" }}>
                    GENEL TOPLAM
                  </TableCell>
                  <TableCell className="fb-total-value" data-label="Bakiye" align="right" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: totalBakiye < 0 ? "#166534" : "#d32f2f" }}>
                    {totalBakiye.toFixed(2)} Gram
                  </TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detay Modal */}
      <Modal open={!!selectedFirma} onClose={() => setSelectedFirma(null)}>
        <Box className="fb-detail-modal" sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", lg: 1200 },
          maxHeight: "90vh",
          background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)",
          borderRadius: 3,
          border: "1px solid rgba(212,175,98,0.28)",
          boxShadow: "0 28px 52px rgba(15,23,42,0.28)",
          p: 4,
          overflow: "hidden", 
          display: "flex",
          flexDirection: "column"
        }}>
          <Box className="fb-detail-header" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" color="#0f172a">
              {selectedFirma?.firmaIsmi} - İşlem Detayları
            </Typography>
            <IconButton onClick={() => setSelectedFirma(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TableContainer className="fb-detail-table-wrap" sx={{ maxHeight: "60vh", overflow: "auto" }}>
            <Table className="fb-detail-table" size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)" }}>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }}>Tarih</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }}>İşlem</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a", minWidth: 150 }} align="center">Tutar & Ödeme T.</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }} align="center">Adet</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }} align="center">Gram</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }} align="center">İşlem Türü</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }} align="center">Has Eşdeğeri (g)</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, bgcolor: "#0f172a" }} align="center">Kaydet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentIslemler.map((i) => (
                  <TableRow key={i.id} hover>
                    <TableCell>{i.tarih}</TableCell>
                    <TableCell>{i.urun}</TableCell>
                    <TableCell align="center">{formatPaymentInfo(i)}</TableCell>
                    <TableCell align="center">{i.adet}</TableCell>
                    <TableCell align="center">{i.gram.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Chip label={i.islemTuru} size="small" color={i.islemTuru.includes("Alış") || i.islemTuru.includes("Giriş") ? "success" : "error"} />
                    </TableCell>
                    <TableCell align="center">
                      {editId === i.id ? (
                        <TextField
                          size="small"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          type="number"
                          inputProps={{ step: "0.01" }}
                          sx={{ width: 100 }}
                        />
                      ) : (
                        <Typography fontWeight="bold" color="#1e40af">
                          {i.hasEsdeger.toFixed(2)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {editId === i.id ? (
                        <IconButton size="small" color="primary" onClick={() => handleSaveHasEsdeger(i.id)}>
                          <SaveIcon />
                        </IconButton>
                      ) : (
                        <IconButton size="small" color="primary" onClick={() => {
                          setEditId(i.id);
                          setEditValue(i.hasEsdeger.toString());
                        }}>
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination 
                count={pageCount} 
                page={currentPage} 
                onChange={handlePageChange} 
                color="primary" 
                showFirstButton 
                showLastButton
              />
            </Box>
          )}

        </Box>
      </Modal>
      <style>{`
        /* --- MOBİL: Firma Bakiyeler (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 1649px), (max-width: 1400px) and (any-pointer: coarse) {
          .fb-page {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            overflow-x: hidden !important;
            padding: 6px 2px 80px !important;
            box-sizing: border-box !important;
          }

          .fb-sube-wrap {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
            margin-bottom: 12px !important;
          }

          .fb-sube-wrap .fb-sube-btn {
            width: 100% !important;
            min-height: 44px !important;
            margin: 0 !important;
          }

          .fb-title {
            font-size: 1.15rem !important;
            line-height: 1.3 !important;
            margin-bottom: 10px !important;
            padding: 0 4px !important;
          }

          .fb-toolbar {
            justify-content: stretch !important;
            margin-top: 0 !important;
            margin-bottom: 12px !important;
          }

          .fb-share-btn {
            width: 100% !important;
            min-height: 40px !important;
          }

          .fb-table-wrap {
            max-height: none !important;
            overflow: visible !important;
            border-radius: 12px !important;
          }

          .fb-table {
            min-width: 0 !important;
          }

          .fb-table thead {
            display: none !important;
          }

          .fb-table tbody tr:not(.fb-total-row) {
            display: block !important;
            margin-bottom: 10px !important;
            border: 1px solid rgba(212, 175, 98, 0.22) !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            background: rgba(255, 255, 255, 0.95) !important;
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
          }

          .fb-table tbody tr:not(.fb-total-row):nth-of-type(odd),
          .fb-table tbody tr:not(.fb-total-row):nth-of-type(even) {
            background-color: rgba(255, 255, 255, 0.95) !important;
          }

          .fb-table tbody tr:not(.fb-total-row) td {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 10px !important;
            padding: 10px 12px !important;
            border: none !important;
            border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
            text-align: right !important;
            font-size: 13px !important;
            word-break: break-word !important;
          }

          .fb-table tbody tr:not(.fb-total-row) td:last-child {
            border-bottom: none !important;
          }

          .fb-table tbody tr:not(.fb-total-row) td::before {
            content: attr(data-label) !important;
            font-weight: 700 !important;
            color: #64748b !important;
            font-size: 12px !important;
            text-align: left !important;
            flex-shrink: 0 !important;
          }

          .fb-total-row {
            display: block !important;
            margin-top: 4px !important;
            border: 2px solid rgba(148, 163, 184, 0.8) !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            background: rgba(226, 232, 240, 0.82) !important;
          }

          .fb-total-row td {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            padding: 12px !important;
            border: none !important;
            font-size: 14px !important;
          }

          .fb-total-row td[colspan] {
            display: none !important;
          }

          .fb-total-row .fb-total-label::before {
            content: attr(data-label) !important;
            font-weight: 700 !important;
            color: #64748b !important;
            font-size: 12px !important;
            margin-right: auto !important;
          }

          .fb-total-row .fb-total-value::before {
            content: attr(data-label) !important;
            font-weight: 700 !important;
            color: #64748b !important;
            font-size: 12px !important;
            margin-right: auto !important;
          }

          .fb-detail-modal {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            transform: none !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 100svh !important;
            max-height: 100svh !important;
            border-radius: 0 !important;
            padding: 12px 8px 16px !important;
            box-sizing: border-box !important;
          }

          .fb-detail-header {
            margin-bottom: 12px !important;
            gap: 8px !important;
          }

          .fb-detail-header .MuiTypography-h5 {
            font-size: 1rem !important;
            line-height: 1.3 !important;
            flex: 1 !important;
            min-width: 0 !important;
          }

          .fb-detail-table-wrap {
            max-height: none !important;
            flex: 1 !important;
            min-height: 0 !important;
            overflow-x: auto !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }

          .fb-detail-table {
            min-width: 920px !important;
          }

          .fb-detail-table .MuiTableCell-root {
            font-size: 12px !important;
            padding: 6px 4px !important;
          }

          .fb-detail-table .MuiTextField-root {
            width: 80px !important;
          }

          .fb-detail-table .MuiInputBase-input {
            font-size: 16px !important;
          }

          .fb-detail-modal .MuiPagination-root {
            margin-top: 12px !important;
          }

          .fb-detail-modal .MuiPagination-ul {
            flex-wrap: wrap !important;
            justify-content: center !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default FirmaBakiyeler;